import { useEffect, useRef } from 'react';
import styles from './HubSpotToast.module.css';

// success shape: { type: 'success', pushed, updated, skipped, failed, failReason, listName }
// error shape:   { type: 'error', message }
//
// "failed" and "skipped" are different facts and get different words. A contact is
// skipped when we chose not to send it (a batch row that was never found); it failed
// when we tried and HubSpot, or our own token, said no. The old toast folded both into
// "skipped" under a green tick, so a push where every row failed read as a success.
export default function HubSpotToast({ toast, onDismiss }) {
  const timerRef = useRef(null);

  const allFailed = !!toast && toast.type !== 'error' && (toast.failed || 0) > 0
    && (toast.pushed || 0) + (toast.updated || 0) === 0;
  const isError = !!toast && (toast.type === 'error' || allFailed);

  useEffect(() => {
    if (!toast) return;
    // A failure stays up longer: the user needs time to read the reason and act on it.
    timerRef.current = setTimeout(onDismiss, isError ? 14000 : 8000);
    return () => clearTimeout(timerRef.current);
  }, [toast, onDismiss, isError]);

  if (!toast) return null;

  let headline, lines;
  if (toast.type === 'error') {
    headline = 'Push failed';
    lines = [toast.message];
  } else if (allFailed) {
    headline = 'Push failed';
    lines = [`${toast.failed} ${toast.failed !== 1 ? 'contacts' : 'contact'} failed${toast.failReason ? `: ${toast.failReason}` : ''}`];
    if (toast.skipped) lines.push(`${toast.skipped} skipped (not verified, not pushed)`);
  } else {
    headline = toast.failed ? 'HubSpot push finished with problems' : 'HubSpot push complete';
    lines = [];
    if (toast.pushed)   lines.push(`${toast.pushed} contact${toast.pushed !== 1 ? 's' : ''} added`);
    if (toast.updated)  lines.push(`${toast.updated} already in HubSpot, not re-pushed`);
    if (toast.failed)   lines.push(`${toast.failed} failed${toast.failReason ? `: ${toast.failReason}` : ''}`);
    if (toast.skipped)  lines.push(`${toast.skipped} skipped (not verified, not pushed)`);
    if (!lines.length)  lines.push('Nothing to sync');
    if (toast.listName) lines.push(`Saved to "${toast.listName}"`);
  }

  return (
    <div className={`${styles.toast} ${isError ? styles.toastError : styles.toastSuccess}`} role="status" aria-live="polite">
      <div className={styles.icon}>{isError ? '✕' : '✓'}</div>
      <div className={styles.body}>
        <p className={styles.headline}>{headline}</p>
        {lines.map((l, i) => (
          <p key={i} className={styles.line}>{l}</p>
        ))}
      </div>
      <button className={styles.close} onClick={onDismiss} aria-label="Dismiss">✕</button>
    </div>
  );
}
